import React, { useState, useRef, useEffect, forwardRef } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Paper,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "./ButtonPrimary";
import { CompDropdownNew } from "./DropdownNew";
import { CompTextFieldNew } from "./TextField";
import { CompSearchInputNew } from "./SearchInputNew";
import { getSortedLocations } from "../../modules/function/function.actions";
import { CompCheckbox } from "../Checkbox";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSION_CREATE_FUNCTION_BEHALF_OF_ANOTHER } from "../../permissions";
import { handleCurrentDateCalulcations } from "../../modules/function/components/functionFormComponentUtils";
import PropTypes from "prop-types";
import { getOrganizations } from "../organizationUtils";

const CompDialog = forwardRef((props, ref) => {
    const { 
        open, 
        onClose, 
        onTemplateSelected,
        organizationTree
    } = props;

    const { t } = useTranslation('translation');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const searchInputRef = useRef();
    const templates = useSelector(state => state.func.templates);
    const locations = useSelector(state => state.location.locations || state.func.locations);
    const [organizationList, setOrganizationList] = useState([])
    const authenticatedUser = useSelector(state => state.auth.authenticatedUser);
    const userMunicipality = useSelector(state => state.municipality.selectedMunicipality);
    const [searchName, setSearchName] = useState("");
    const [filterLocationId, setFilterLocationId] = useState("");
    const [filterOrganizationId, setFilterOrganizationId] = useState("");
    const [checkboxState, setCheckboxState] = useState({ searchOnlyUserGroups: false });
    const [filteredList, setFilteredList] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [filterTemplateType, setFilterTemplateType] = useState("");
    const [isVisible, setIsVisible] = useState(true)

    const templateTypeOptions = [
        { id: 'public', name: t('function.public_template') },
        { id: 'group', name: t('function.group_template') },
        { id: 'private', name: t('function.private_template') }
    ];

    const searchOnlyUserGroupsOption = [
        {
            key: "searchOnlyUserGroups",
            label: /* t('function.search_all_templates') ||  */"Näytä toimintapohjat jossa olet mukana", 
            value: checkboxState.searchOnlyUserGroups
        }
    ];

    useEffect(() => {
        if(authenticatedUser.role === 'ROLE_EMPLOYEE_MUNICIPALITY' || authenticatedUser.role === 'ROLE_EMPLOYEE_ORGANIZATION') {
            setIsVisible(false)
        } else {
            setIsVisible(true)
        }
    })

    useEffect(() => {
        if (filterTemplateType !== 'group') {
            setCheckboxState({ searchAll: false });
        }
    }, [filterTemplateType]);

    useEffect(() => {
        setOrganizationList(getOrganizations(organizationTree));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationTree])

    useEffect(() => {
        if (open) {
            setSearchName("");
            setFilterLocationId("");
            setFilterOrganizationId("");
            setCheckboxState({ searchOnlyUserGroups: false });
            setSelectedRowIndex(null);
            setSelectedTemplate(null);
            setFilterTemplateType(""); 
            
            searchInputRef.current?.closeSearchList();

            const formattedResults = templates.map(item => item.template ? item : { template: item });
            setFilteredList(formattedResults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, templates]);

    const handleTemplateTypeChange = (event) => {
        setFilterTemplateType(event.target.value);
    };

    const handleFilterNameChange = (event) => {
        setSearchName(event.target.value);
    };

    const handleLocationChange = (event) => {
        setFilterLocationId(event.target.value);
    };

    const handleOrganizationChange = (event) => {
        setFilterOrganizationId(event.target.value);
    };

    const handleRowSelect = (template, index) => {
        setSelectedRowIndex(index);
        setSelectedTemplate(template);
    };

    const handleReset = () => {
        setSearchName("");
        setFilterLocationId("");
        setFilterOrganizationId("");
        setCheckboxState({ searchOnlyUserGroups: false }); 
        setSelectedRowIndex(null);
        setSelectedTemplate(null);
        setFilterTemplateType("");

        searchInputRef.current?.closeSearchList();

        const formattedResults = templates.map(item => item.template ? item : { template: item });
        setFilteredList(formattedResults);
    };

    const handleApplyFilters = () => {
        const nameTerm = searchName.trim().toLowerCase();
        
        if (nameTerm === '' && !filterLocationId && !filterOrganizationId && !filterTemplateType && !checkboxState.searchOnlyUserGroups) {
            alert(t('function.fill_fields_to_search') || 'Täytä kentät hakeaksesi toimintapohjia');
            return;
        }

        const results = templates.filter(item => {
            const template = item.template || item; 
            const { templateName, locationId, organizationId, municipalityTemplate, templateHandlers } = template;

            const matchName = nameTerm ? templateName.toLowerCase().includes(nameTerm) : true;
            const matchLocation = filterLocationId ? locationId === parseInt(filterLocationId) : true;
            const matchOrganization = filterOrganizationId ? organizationId === parseInt(filterOrganizationId) : true;
            
            let currentTemplateType = 'private';
            if (municipalityTemplate) {
                currentTemplateType = 'public';
            } else if (templateHandlers && templateHandlers.length > 1) {
                currentTemplateType = 'group';
            }

            const matchTemplateType = filterTemplateType ? currentTemplateType === filterTemplateType : true;
            
            const hasUserAccess = templateHandlers && templateHandlers.some(handler => handler.id === authenticatedUser.id);
            const matchUserAccess = checkboxState.searchOnlyUserGroups ? hasUserAccess : true;

            return matchName && matchLocation && matchOrganization && matchTemplateType && matchUserAccess;
        });

        const formattedResults = results.map(item => item.template ? item : { template: item });
        
        setFilteredList(formattedResults);
        setSelectedRowIndex(null);
        setSelectedTemplate(null);
    };

    const handleConfirm = () => {
        if (!selectedTemplate) return;

        const permissionToCreateAnother = hasPermission(PERMISSION_CREATE_FUNCTION_BEHALF_OF_ANOTHER, authenticatedUser);
        const isOwner = selectedTemplate.creatorId === authenticatedUser.id;

        let showDelete = false;
        let showEdit = false;
        let showRemoveMe = false;

        if (permissionToCreateAnother) {
            showDelete = true;
            showEdit = true;
            const handlers = selectedTemplate.templateHandlers || [];
            const found = handlers.some((t) => t.id === authenticatedUser.id) 
                          && (handlers.length > 1 || !isOwner);
            showRemoveMe = found && !selectedTemplate.municipalityTemplate;
        } else if (isOwner) {
            showDelete = true;
            showEdit = true;
            const handlers = selectedTemplate.templateHandlers || [];
            showRemoveMe = handlers.length > 1 && !selectedTemplate.municipalityTemplate;
        } else {
            showRemoveMe = !selectedTemplate.municipalityTemplate;
        }

        const newTemplateData = { ...selectedTemplate };
        const { id, ...data } = structuredClone(selectedTemplate);

        if (!permissionToCreateAnother) {
            if (!data.organizers) data.organizers = [];
            if (!data.organizers.find((o) => o.id === authenticatedUser.id)) {
                data.organizers.unshift(authenticatedUser);
            }
        }

        handleCurrentDateCalulcations(data);

        if (onTemplateSelected) {
            onTemplateSelected({
                data: data,
                originalTemplate: newTemplateData,
                uiFlags: {
                    showDelete,
                    showEdit,
                    showRemoveMe
                }
            });
        }
        
        onClose();
    };

    const handleCheckboxChange = (event) => {
        setCheckboxState({
            ...checkboxState,
            searchOnlyUserGroups: event.target.checked
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('function.search_template')}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, sm: 6}}>
                        <CompTextFieldNew
                            label={t('common.municipality')}
                            isLabelShrink={true}
                            value={userMunicipality?.name || ""}
                            disabled={true}
                            name="municipalityId"
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <CompSearchInputNew
                            isLabelShrink={true}
                            ref={searchInputRef}
                            label={t('function.filter')}
                            onChange={handleFilterNameChange}
                            value={searchName}
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <CompDropdownNew
                            label={t('function.location')}
                            selectedValue={filterLocationId}
                            rows={getSortedLocations(locations, organizationList, authenticatedUser)}
                            name="locationId"
                            labelColumn="name"
                            isClearOption={true}
                            className={'function-dropdown'}
                            valueColumn="id"
                            onChange={handleLocationChange}
                            hasEmptyOption={false}
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <CompDropdownNew
                            label={t('common.organization')}
                            valueColumn={"id"}
                            className={'function-dropdown-organization filter-organization'}
                            labelColumn={"name"}
                            rows={organizationList}
                            hasEmptyOption={true}
                            onChange={handleOrganizationChange}
                            name="organizationId"
                            selectedValue={filterOrganizationId}
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <CompDropdownNew
                            label={t('common.users')} 
                            valueColumn="id"
                            labelColumn="name"
                            className="function-dropdown filter-template-type"
                            rows={templateTypeOptions}
                            hasEmptyOption={false}
                            onChange={handleTemplateTypeChange}
                            name="templateType"
                            selectedValue={filterTemplateType}
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        {isVisible && 
                            <CompCheckbox
                                booleanmode={true}
                                visible={false}
                                disabled={filterTemplateType !== 'group'}
                                rows={searchOnlyUserGroupsOption}
                                selected={checkboxState}
                                onChange={handleCheckboxChange}
                                name="searchOnlyUserGroupsCheckbox"
                            />
                        }
                    </Grid>
                </Grid>

                <Box sx={{ marginTop: 2 }}>
                    {isMobile ? (
                        <Box role="table" aria-label="toimintapohjataulukko">
                            {filteredList.map((item, index) => (
                                <Paper
                                    key={index}
                                    tabIndex={0}
                                    role="row"
                                    className="box_paper-wrapper"
                                    aria-selected={selectedRowIndex === index + 1}
                                    onClick={() => handleRowSelect(item.template, index + 1)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleRowSelect(item.template, index + 1);
                                        }
                                    }}
                                    sx={{
                                        mb: 2, p: 2, cursor: "pointer",
                                        border: selectedRowIndex === index + 1
                                            ? "2px solid " + theme.palette.primary.main
                                            : "1px solid #ddd",
                                        "&:focus": {
                                            outline: `2px solid ${theme.palette.primary.main}`,
                                            outlineOffset: "2px",
                                        }
                                    }}
                                >
                                    <Box role="cell">
                                        <Typography variant="h6" className="mobile_template-item" fontWeight="bold">
                                            {item.template.templateName}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>{t('common.locations')}:</strong> {item.template.locationName}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>{t('common.organization')}:</strong>{" "}
                                            {organizationList.find(obj => obj.id === item.template.organizationId)?.name?.trim() ?? ""}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>{t('common.users')}:</strong>{" "}
                                            {{
                                                all: t('function.all'),
                                                open: t('function.open'),
                                                group: t('function.group'),
                                                individual: t('function.individual'),
                                            }[item.template.eventType] || item.template.eventType}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper} className={'CompSystemLogTable-container'}>
                            <Table sx={{ tableLayout: "fixed", width: "100%" }} aria-label="template table">
                                {filteredList.length > 0 && (
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" sx={{ width: '40%' }} className="table-header">{t('function.template_name')}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }} className="table-header">{t('common.locations')}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }} className="table-header">{t('common.organization')}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }} className="table-header">{t('common.users')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                )}
                                <TableBody>
                                    {filteredList.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            tabIndex={0}
                                            role="row"
                                            hover
                                            selected={selectedRowIndex === index + 1}
                                            aria-selected={selectedRowIndex === index + 1}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleRowSelect(item.template, index + 1);
                                                }
                                            }}
                                            onClick={() => handleRowSelect(item.template, index + 1)}
                                            sx={{
                                                cursor: "pointer", width: '100%',
                                                "&:focus": { backgroundColor: "#E9EBFF" }
                                            }}
                                        >
                                            <TableCell align="left" sx={{ width: '40%' }}>{item.template.templateName}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }}>{item.template.locationName}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }}>{organizationList.find(obj => obj.id === item.template.organizationId)?.name?.trim() ?? ""}</TableCell>
                                            <TableCell align="left" sx={{ width: '20%' }}>
                                                {item.template.municipalityTemplate
                                                    ? t('function.public_template')
                                                    : item.template.templateHandlers && item.template.templateHandlers.length > 1
                                                        ? t('function.group_template')
                                                        : t('function.private_template')
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Grid container sx={{ width: 1 }} className="template_btn-wrapper">
                    <Grid size={{xs: 12, sm: 6}} sx={{ display: 'flex', flexDirection: { sm: 'row', xs: 'column' } }}>
                        <ButtonPrimary type="button" onClick={handleReset}>{t('function.reset_all')}</ButtonPrimary>
                        <ButtonPrimary type="button" onClick={onClose}>{t('common.cancel')}</ButtonPrimary>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}} sx={{ display: 'flex', flexDirection: { sm: 'row', xs: 'column' } }}>
                        <ButtonPrimary type="button" variant="contained" onClick={handleApplyFilters}>{t('function.search_template')}</ButtonPrimary>
                        <ButtonPrimary 
                            disabled={!selectedTemplate} 
                            type="button" 
                            onClick={handleConfirm}
                        >
                            {t('function.choose_template')}
                        </ButtonPrimary>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
});
CompSearchInputNew.propTypes = {
    keyColumn: PropTypes.string,
    valueColumn: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.any)
    ]),
    rows: PropTypes.array,
    selectedRows: PropTypes.array,
    multiselect: PropTypes.bool,
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.string
};

export {CompDialog}